import * as React from "react";
import { createLink } from "@tanstack/react-router";
import { Button  } from "@mantine/core";
import type {ButtonProps} from "@mantine/core";
import type { LinkComponent, LinkProps } from "@tanstack/react-router";

export interface ButtonLinkProps
  extends Omit<ButtonProps, "href" | "children">,
    Omit<LinkProps, "href" | "children"> {
  /**
   * Preload strategy for the link
   * @default "intent"
   */
  preload?: false | "intent";
  children?: React.ReactNode | ((state: { isActive: boolean; isTransitioning: boolean }) => React.ReactNode);
}

const MantineButtonComponent = React.forwardRef<
  HTMLAnchorElement,
  ButtonLinkProps
>(({ preload = "intent", children, ...props }, ref) => {
  // Handle function children from Link component
  const resolvedChildren = typeof children === 'function' 
    ? children({ isActive: false, isTransitioning: false })
    : children;
  
  return <Button ref={ref} component="a" {...props}>{resolvedChildren}</Button>;
});

MantineButtonComponent.displayName = "MantineButtonComponent";

const CreatedLinkComponent = createLink(MantineButtonComponent);

/**
 * ButtonLink - A reusable button component that navigates using TanStack Router.
 *
 * Best practices:
 * - Use for buttons that navigate (instead of onClick + useNavigate)
 * - Provides href for cmd/ctrl+click to open in new tab
 * - Supports preloading for better UX
 *
 * @example
 * ```tsx
 * <ButtonLink to="/dashboard" variant="filled">
 *   Go to Dashboard
 * </ButtonLink>
 * <ButtonLink to="/posts/$postId" params={{ postId: "123" }} size="sm">
 *   View Post
 * </ButtonLink>
 * ```
 */
export const ButtonLink: LinkComponent<typeof MantineButtonComponent> = (
  props
) => {
  return <CreatedLinkComponent preload="intent" {...props} />;
};
